import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconInfoCircle } from "@tabler/icons-react";
import { SecondaryButton } from "../../Buttons/SecondaryButton";
import { GroupedNumberInput } from "../../Inputs/GroupedNumberInput";
import { HoverInfo } from "../../HoverInfo/HoverInfo";
import { PDeleteConfirmation } from "../../PDeleteConfirmation/PDeleteConfirmation";
import { CAPACITY_DECISION_PENDING } from "@/lib/api/errors/ErrorHandler";

/**
 * How many placeholder rows one click may create.
 *
 * Not a backend limit — `POST /guests/{eventId}/batch` takes the list as it
 * comes, with no `@Size` and no rate limit — but a typo guard: this field sits
 * next to a number pad, "50" and "500" are one keystroke apart, and every row
 * costs a seat, a contingent unit (one-way in this product) and a line in the
 * guest list that somebody has to delete again by hand. An organiser who
 * genuinely needs more clicks twice.
 */
export const MAX_PLACEHOLDER_COMPANIONS = 50;

type PlaceholderCompanionsProps = {
  /**
   * Creates `count` placeholder companions. May throw — a rejected create is
   * rendered inline here, right under the field that caused it.
   */
  onCreateAction: (count: number) => Promise<void>;
  /** Called after a create that succeeded (or that handed off to the capacity
   *  popup), so the host can close the form it lives in. */
  onDoneAction: () => void;
  /** No contingent left ⇒ the server refuses the create anyway. Same gate the
   *  guest list puts on its own two create buttons. */
  disabled?: boolean;
  /**
   * The form around this row holds unsaved input, so a successful quick-add —
   * which closes that form — would throw it away. Ask first.
   *
   * Passed in rather than sensed here: only the form knows whether anything
   * was typed into it (`hasInput`), and only it knows that closing unmounts
   * the entered fields with no draft anywhere.
   */
  confirmDiscard?: boolean;
};

/**
 * "Only the number is known" — creates N nameless companions in one request.
 *
 * The party size is often decided before the names are: a guest replies "we'll
 * be four", and the operator needs four rows now (they carry the capacity, the
 * contingent and the seats) with the names filled in later. Typing four empty
 * forms to get there was the whole complaint.
 *
 * The rows are named "TBD 1", "TBD 2", … by the caller — deliberately NOT left
 * blank: a nameless row is indistinguishable from its neighbour in the guest
 * list, in seating and at the door, and the number is what lets the operator
 * say which of them they just renamed.
 */
export const PlaceholderCompanions: React.FC<PlaceholderCompanionsProps> = ({
  onCreateAction,
  onDoneAction,
  disabled = false,
  confirmDiscard = false,
}) => {
  const t = useTranslations("popup-create-guest");
  const t2 = useTranslations("errors");
  const [count, setCount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /**
   * In-flight latch, as a ref rather than the `loading` state.
   *
   * The disabled button below is not enough on its own: two clicks dispatched
   * before React re-renders both read `loading === false`, and each one mints a
   * WHOLE BATCH. Worse, the second batch is built from the same companion list
   * (the append only happens once the first request resolves), so it repeats
   * "TBD 1..n" instead of continuing — duplicate names, duplicate seats,
   * duplicate contingent, and the operator can no longer tell which "TBD 2"
   * they just renamed. Measured: three clicks during one in-flight request
   * produced three identical batches.
   */
  const inFlight = useRef(false);
  /** A count the operator asked for, waiting on the discard confirmation. */
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const submit = async () => {
    if (inFlight.current || disabled) return;
    // `GroupedNumberInput` emits a canonical digit string, so this is a plain
    // parse — no `1e1` reading as ten here and as one in the request.
    const parsed = Number.parseInt(count, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > MAX_PLACEHOLDER_COMPANIONS) {
      setError(t("placeholderCompanionsRange", { max: MAX_PLACEHOLDER_COMPANIONS }));
      return;
    }
    if (confirmDiscard) {
      // The operator started typing the one companion whose name they DO know,
      // then remembered the other three are unnamed. Creating the placeholders
      // closes this form and takes that half-typed guest with it.
      setPendingCount(parsed);
      return;
    }
    await create(parsed);
  };

  const create = async (parsed: number) => {
    if (inFlight.current) return;
    setError(null);
    inFlight.current = true;
    setLoading(true);
    try {
      await onCreateAction(parsed);
      setCount("");
      onDoneAction();
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown";
      // Event full → GuestsProvider has the 3-option capacity popup open on top
      // of this form. Close silently, exactly like the full create does; the
      // decision (and its outcome) belongs to that popup now.
      if (message === CAPACITY_DECISION_PENDING) {
        setCount("");
        onDoneAction();
        return;
      }
      setError(t2(message));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="placeholder-companions"
      className="rounded-xl border outline-tertiary p-3 mb-4"
    >
      <div className="flex flex-row flex-wrap items-end gap-3">
        <div className="grow min-w-40">
          <GroupedNumberInput
            id="placeholder-companions-count"
            label={t("placeholderCompanionsLabel")}
            value={count}
            disabled={disabled || loading}
            maxLengthDigits={3}
            // The refusal rides FormInput's own `error`, which renders it with
            // `role="alert"` and wires `aria-invalid` + `aria-describedby` onto
            // the input. The hand-rolled <p> this replaces said the same words
            // to sighted users and nothing at all to a screen reader.
            error={error ?? undefined}
            onChangeAction={(value) => {
              setCount(value);
              if (error) setError(null);
            }}
            onEnterAction={() => void submit()}
          />
        </div>
        {/* SECONDARY, not primary: the dialog's own "Create guest" at the
            bottom is its primary action, and two `cta-primary` buttons in one
            form make neither of them the answer. Same reasoning as the
            contingent banner's deliberately recoloured Upgrade button. */}
        <SecondaryButton
          text={t("placeholderCompanionsCreate")}
          size="small"
          width="w-auto"
          // Matches the field beside it, which is `h-9`; the default `h-max`
          // makes the pair read as misaligned.
          height="h-9"
          testId="placeholder-companions-create"
          loading={loading}
          disabled={disabled || loading}
          onClick={() => void submit()}
        />
      </div>
      {/* What "TBD" means, on a real focusable target. `FormInput`'s `info`
          prop — what this replaces — renders NO glyph; it only makes the label
          `cursor-help` on pointer hover, so on a phone and for anybody
          navigating by keyboard the one sentence explaining the button was
          unreachable. It matters more than usual here: "TBD" is an English
          abbreviation the button carries untranslated into every locale. */}
      <HoverInfo
        message={t("placeholderCompanionsInfo")}
        className="mt-2 inline-block"
      >
        <button
          type="button"
          data-testid="placeholder-companions-info"
          aria-label={t("placeholderCompanionsInfo")}
          className="inline-flex items-center gap-1 font-tertiary body-3-regular focus-ring rounded-md"
        >
          <IconInfoCircle size={14} strokeWidth={1.5} className="shrink-0" />
          {t("placeholderCompanionsWhat")}
        </button>
      </HoverInfo>
      {pendingCount !== null && (
        <PDeleteConfirmation
          title={t("placeholderCompanionsDiscardTitle")}
          message={t("placeholderCompanionsDiscardBody")}
          primaryButtonText={t("placeholderCompanionsDiscardConfirm")}
          isLoading={loading}
          onCloseAction={() => setPendingCount(null)}
          onConfirmAction={async () => {
            const next = pendingCount;
            setPendingCount(null);
            if (next !== null) await create(next);
          }}
        />
      )}
    </div>
  );
};
